'use strict'

// React
import {useCallback, useEffect, useState} from 'react'

// Commerce SDK
import {useCustomer, useCustomerId, useCustomerType} from '@salesforce/commerce-sdk-react'

// OOTB Hooks
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

// Hooks
import {useCurrentBasket} from './use-current-basket'
import useShipToCountry from './use-ship-to-country'
import useMultiSite from './use-multi-site'

// Custom Utils
import {
    buildAnalyticsPageViewData,
    buildAnalyticsRemoveFromCartData,
    buildAnalyticsSignUpEventData,
    buildAnalyticsLoginEventData,
    buildAnalyticsCheckoutData,
    buildAnalyticsNewsletterSubscriptionData,
    buildAnalyticsSearchEventData,
    buildAnalyticsClickRelatedProductsData,
    buildAnalyticsCartViewData,
    buildAnalyticsAddToWishlistData,
    buildAnalyticsViewItemListData,
    buildAnalyticsAddtoCartData,
    buildAnalyticsClickMenuData,
    buildAnalyticsViewItemData
} from '../utils/analytics-utils'

import {getLocalStorageJSONItem, setLocalStorageJSONItem} from '../utils/utils'

// Custom Contexts
import {useConversionRate} from '../custom-contexts'

/**
 * Custom hook for integrating Google Tag Manager (GTM) functionality within a React component.
 * Provides methods to trigger GTM events, including page view events, with relevant customer and page details.
 *
 * @returns {Object} An object containing methods to interact with GTM.
 * @returns {Function} return.triggerPageLoadEvent - A function to trigger a GTM page view event.
 *
 * @example
 * // Example usage within a React component
 * const { triggerPageLoadEvent } = useGTM();
 *
 * useEffect(() => {
 *     triggerPageLoadEvent();
 * }, [triggerPageLoadEvent]);
 */
export const useGTM = () => {
    const customerId = useCustomerId()
    const {isRegistered, customerType} = useCustomerType()
    const {data: customer, isLoading: isCustomerLoading} = useCustomer(
        {
            parameters: {customerId}
        },
        {enabled: !!customerId && isRegistered}
    )
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {shipToCountry} = useShipToCountry()
    const {locale, site} = useMultiSite()
    const {currency: activeCurrency} = useCurrency()
    const conversionsRate = useConversionRate()

    // State
    const [pageType, setPageType] = useState(null)

    /**
     * Triggers a Google Tag Manager (GTM) event by updating the data layer with the specified event name and data.
     * This function clears any previous ecommerce data from the data layer before adding the new event details.
     *
     * @function
     * @param {string} eventName - The name of the event to be triggered in GTM.
     * @param {Object} [eventData={}] - An optional object containing additional data to be included in the GTM event.
     * @returns {void}
     */
    const triggerGTMEvent = useCallback((eventName, eventData = {}) => {
        // Clear the previous ecommerce object.
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
            ecommerce: null
        })

        // Send the new event to the data layer.
        window.dataLayer.push({
            event: eventName,
            ...eventData
        })
    }, [])

    /**
     * Triggers a Google Tag Manager (GTM) event for viewing a list of items.
     * This function processes a list of products in batches and sends each batch
     * as a separate GTM event to avoid exceeding the maximum number of items per event.
     *
     * @param {Array} products - An array of product objects to be included in the GTM event.
     * @param {number} maxItemsPerEvent - The maximum number of items allowed per GTM event batch.
     * @param {number} totalProducts - The total number of products to be processed.
     * @param {string} currency - The currency code used for the transaction, derived from the basket or active currency.
     *
     * @returns {void} This function does not return a value.
     */
    const triggerViewItemListEvent = (products, format) => {
        const maxItemsPerEvent = 15
        const totalProducts = products?.length ?? 0

        for (let i = 0; i < totalProducts; i += maxItemsPerEvent) {
            const productBatch = products.slice(i, i + maxItemsPerEvent)

            const finalEventData = {
                ecommerce: buildAnalyticsViewItemListData(
                    productBatch,
                    basket,
                    conversionsRate,
                    format
                )
            }

            triggerGTMEvent('view_item_list', finalEventData)
        }
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for removing a product from the cart.
     *
     * This function constructs the necessary analytics data for a 'remove from cart' event
     * and triggers the corresponding GTM event with the constructed data.
     *
     * @param {Object} product - The product object containing details about the product being removed.
     * @param {Object} productItem - The specific item instance of the product being removed from the cart.
     */
    const triggerRemoveFromCartEvent = (product, productItem) => {
        const finalEventData = {
            ecommerce: buildAnalyticsRemoveFromCartData(product, productItem, conversionsRate)
        }
        triggerGTMEvent('remove_from_cart', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for removing a product from the cart.
     *
     * This function constructs the necessary data for the 'remove_from_cart' event
     * and sends it to GTM using the `triggerGTMEvent` function. It utilizes the
     * `buildAnalyticsRemoveFromCartData` function to gather and format the required
     * analytics data for the specified product and product item.
     *
     * @param {Object} product - The product object containing details about the product to be removed.
     * @param {Object} productItem - The specific item of the product being removed, which may include additional details.
     */
    const triggerAddToCartEvent = (productItems, finalProduct) => {
        const finalEventData = {
            ecommerce: buildAnalyticsAddtoCartData(
                productItems,
                finalProduct,
                conversionsRate,
                basket
            )
        }
        triggerGTMEvent('add_to_cart', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for adding a product to the wishlist.
     *
     * This function constructs the necessary event data for an "add_to_wishlist" event
     * and sends it to GTM. It utilizes the `buildAnalyticsAddToWishlistData` function
     * to generate the appropriate ecommerce data structure for the event.
     *
     * @param {Object} product - The product object containing details about the product
     *                            being added to the wishlist. This object is used to
     *                            build the analytics data for the GTM event.
     */
    const triggerAddtoWishListEvent = (product) => {
        const finalEventData = {
            ecommerce: buildAnalyticsAddToWishlistData(product, conversionsRate)
        }
        triggerGTMEvent('add_to_wishlist', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for newsletter subscription.
     *
     * This function constructs the event data for a newsletter subscription
     * using the provided page position and sends it to GTM.
     *
     * @param {string} pagePosition - The position on the page where the newsletter subscription event is triggered.
     */
    const triggerNewsletterSubscriptionEvent = (pagePosition) => {
        const finalEventData = buildAnalyticsNewsletterSubscriptionData(pagePosition)
        triggerGTMEvent('newsletter_subscription', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for related product clicks.
     *
     * This function constructs the necessary analytics data for a related product click event
     * and triggers the GTM event with the specified event name and data.
     *
     * @param {Object} product - The product object containing details of the related product.
     * @param {string} event - The name of the event to be triggered in GTM.
     */
    const triggerClickRelatedProductsEvent = (product, event) => {
        const finalEventData = buildAnalyticsClickRelatedProductsData(product)
        triggerGTMEvent(event, finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event when the shipping destination country changes.
     *
     * This function checks if the old and new shipping destination countries are valid strings
     * and not null. If both conditions are met, it converts the country names to lowercase
     * and triggers a GTM event named 'popin_change_shipto' with the details of the country change.
     *
     * @param {string} oldShipToCountry - The previous shipping destination country.
     * @param {string} newShipToCountry - The new shipping destination country.
     */
    const triggerPopinChangeShipToEvent = (oldShipToCountry, newShipToCountry) => {
        triggerPageLoadEvent(getLocalStorageJSONItem('lastPageViewType'))
        if (
            typeof oldShipToCountry === 'string' &&
            oldShipToCountry !== null &&
            typeof newShipToCountry === 'string' &&
            newShipToCountry !== null
        ) {
            const oldCountryLower = oldShipToCountry.toLowerCase()
            const newCountryLower = newShipToCountry.toLowerCase()
            triggerGTMEvent('popin_change_shipto', {
                country_change: `${oldCountryLower} to ${newCountryLower}`
            })
        }
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for viewing the cart with the specified products.
     *
     * This function constructs the necessary analytics data for a cart view event and sends it to GTM.
     *
     * @param {Array} products - An array of product objects that are currently in the cart.
     * @returns {void}
     */
    const triggerViewCartEvent = (products) => {
        const finalEventData = {
            ecommerce: buildAnalyticsCartViewData(basket, products, conversionsRate)
        }
        triggerGTMEvent('view_cart', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager (GTM) event for search results.
     *
     * This function constructs the necessary analytics data for a search event
     * using the provided search term and then triggers a GTM event named
     * 'view_search_results' with the constructed data.
     *
     * @param {string} searchTerm - The search term entered by the user to be
     *                              included in the analytics event data.
     */
    const triggerSearchEvent = (searchTerm) => {
        const finalEventData = buildAnalyticsSearchEventData(searchTerm)
        triggerGTMEvent('view_search_results', finalEventData)
    }

    /*
     * Triggers a Google Tag Manager event for guest checkout.
     * This function is used to signal that a guest checkout process has been initiated.
     * It utilizes the `triggerGTMEvent` function to send the event data to GTM.
     *
     * @function
     * @returns {void}
     */
    const triggerGuestCheckoutEvent = () => {
        triggerGTMEvent('guest_checkout', {})
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for the "Shop The Look" feature.
     * This function sends a 'click_shop_the_look' event to GTM with an empty data object.
     * It is used to track user interactions with the "Shop The Look" feature on the website.
     */
    const triggerShopTheLookEvent = () => {
        triggerGTMEvent('click_shop_the_look', {})
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for user sign-up.
     * This function constructs the necessary event data for a sign-up action
     * and sends it to GTM using the specified event name.
     *
     * @function
     * @returns {void}
     */
    const triggerSignUpEvent = () => {
        const finalEventData = buildAnalyticsSignUpEventData()
        triggerGTMEvent('sign_up', finalEventData)
    }

    /*
     * Triggers Google Tag Manager (GTM) events for checkout processes with specified product data.
     *
     * @param {Array} products - An array of product objects involved in the checkout process.
     * @param {string} event - The name of the GTM event to be triggered.
     */
    const triggerCheckoutEvents = (products, event, lineItemCrt) => {
        const finalEventData = {
            ecommerce: buildAnalyticsCheckoutData(
                lineItemCrt ? lineItemCrt : basket,
                products,
                conversionsRate,
                event,
                customerType
            )
        }
        triggerGTMEvent(event, finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for a menu click action.
     *
     * This function constructs the necessary event data for a menu click using the provided category
     * and then triggers a GTM event with the event name 'click_menu'.
     *
     * @param {string} category - The category of the menu item that was clicked. This is used to build
     *                            the analytics data for the event.
     */
    const triggerClickMenuEvent = (category) => {
        const finalEventData = buildAnalyticsClickMenuData(category)
        triggerGTMEvent('click_menu', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager event for a website error.
     *
     * This function sends an event to Google Tag Manager with a predefined
     * error message indicating a '404 page' error. It is used to log and
     * track error occurrences on the website.
     *
     **/
    const triggerErrorEvent = () => {
        triggerGTMEvent('error_website', {error_message: '404 page'})
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for user login.
     *
     * This function constructs the necessary analytics data for a login event
     * and sends it to GTM using the specified event name 'login'.
     *
     * @function
     * @returns {void}
     */
    const triggerLoginEvent = () => {
        const finalEventData = buildAnalyticsLoginEventData()
        triggerGTMEvent('login', finalEventData)
    }

    /**
     * Triggers a Google Tag Manager (GTM) event for viewing an item.
     *
     * This function constructs the necessary analytics data for a 'view_item' event
     * and triggers the corresponding GTM event with the constructed data.
     *
     * @param {Object} product - The product object containing details about the product being viewed.
     */
    const triggerViewItemEvent = (event, product) => {
        const finalEventData = {
            ecommerce: buildAnalyticsViewItemData(product, conversionsRate)
        }
        triggerGTMEvent(event, finalEventData)
    }

    /**
     * Triggers a page load event by setting the page type.
     *
     * @param {string} type - The type of the page to be set, which determines the page load event.
     */
    const triggerPageLoadEvent = (type) => {
        setPageType(type)
    }

    /**
     * Custom React hook that tracks page load events and manages cookie-related GTM (Google Tag Manager) events.
     * Intended for use within the GTM integration layer to ensure proper event firing on page load and cookie changes.
     * @returns {void}
     */
    const usePageLoadAndCookies = (pageName) => {
        const [hasMounted, setHasMounted] = useState(false)
        const isCookiesChanged = getLocalStorageJSONItem('cookiesAccepted')

        useEffect(() => {
            if (!hasMounted) {
                setHasMounted(true)
                triggerPageLoadEvent(pageName)
            } else if (typeof isCookiesChanged !== 'undefined') {
                triggerPageLoadEvent(pageName)
            }
        }, [isCookiesChanged, hasMounted, pageName])
    }

    const [isPageViewTriggered, setIsPageViewTriggered] = useState(false)

    useEffect(() => {
        const isGuest = customerType === 'guest'
        const isCustomerDataReady = !isCustomerLoading || isGuest
        const countryCodeStillSetting = getLocalStorageJSONItem('countryCode')

        if (
            isCustomerDataReady &&
            !isBasketLoading &&
            pageType &&
            basket &&
            !countryCodeStillSetting
        ) {
            const finalEventData = buildAnalyticsPageViewData(
                pageType,
                customer,
                basket,
                shipToCountry,
                locale,
                site,
                customerType,
                activeCurrency
            )
            triggerGTMEvent('page_view', finalEventData)
            setLocalStorageJSONItem('lastPageViewType', pageType)
            setPageType(null) // Reset pageType after event is triggered
            setIsPageViewTriggered(true)
        }
    }, [
        isCustomerLoading,
        isBasketLoading,
        pageType,
        customer,
        basket,
        shipToCountry,
        locale,
        site,
        customerType,
        activeCurrency,
        triggerGTMEvent
    ])

    return {
        triggerPageLoadEvent,
        triggerRemoveFromCartEvent,
        triggerAddtoWishListEvent,
        triggerNewsletterSubscriptionEvent,
        triggerClickRelatedProductsEvent,
        triggerSearchEvent,
        triggerGuestCheckoutEvent,
        triggerSignUpEvent,
        triggerLoginEvent,
        triggerCheckoutEvents,
        triggerViewCartEvent,
        triggerViewItemListEvent,
        triggerAddToCartEvent,
        triggerClickMenuEvent,
        triggerErrorEvent,
        triggerShopTheLookEvent,
        triggerPopinChangeShipToEvent,
        triggerViewItemEvent,
        usePageLoadAndCookies,
        isPageViewTriggered
    }
}
